/**
 * CommunityCoordinationInboxPanel (#454) — the PEOC's MULTI-community coordination inbox: the
 * discovery surface over the #420a threads. Today the PEOC↔community thread is reachable ONLY by
 * clicking one community on the map, so the PEOC cannot see WHICH communities have coordination
 * traffic waiting on it. This lists every community with traffic (unread-for-me OR recently
 * active) and each row OPENS that community's real #420a thread — closing the discoverability gap
 * behind Ontario IMS §4.2 (the PEOC "maintains a regular communication cycle with host and
 * evacuated communities").
 *
 * READ-ONLY: it polls one server-side aggregate (GET /api/communities/comms-inbox) and jumps to
 * the thread; it posts nothing and acknowledges nothing (opening the thread + clicking Acknowledge
 * there stays the ONLY thing that tells a community the PEOC saw a message — the #453 rule). It
 * does NOT auto-emit a receipt on render.
 *
 * HONESTY (unknown ≠ zero, copied from ShiftLogPanel's three-state): a rejected load renders the
 * ERROR state (with retry), NEVER the all-quiet empty state and NEVER a "0" tab badge; loading
 * shows only until the first load resolves (a background refresh keeps the list); loaded+empty is
 * a genuine no-traffic result. The api client THROWS on non-OK precisely so a failure cannot read
 * as "all quiet".
 *
 * FN-DIGNITY (standing): the per-row count is the OPERATOR's OWN not-yet-acknowledged messages —
 * the operator's queue depth, never a watch on the community's behaviour. Every community renders
 * in the SAME neutral card — a First-Nation row is a full peer of a municipal one (no FN-only
 * styling). The surface reuses the #420a "Operator coordination — not resident-facing" disclaimer.
 */
import { useCallback, useEffect, useState, type JSX } from 'react';
import type { CommunityCoordinationInboxRow } from '@tsr/shared-types';
import { fetchCommunityCoordinationInbox } from './api';
import { selectOnCop } from './selectionBridge';
import {
  OPERATOR_COORDINATION_DISCLAIMER,
  hazardProvenanceLabel,
} from './communityCommsVocabulary';
import {
  COMMUNITY_COORDINATION_INTRO,
  COMMUNITY_COORDINATION_LOADING,
  COMMUNITY_COORDINATION_EMPTY,
  COMMUNITY_COORDINATION_ERROR,
  awaitingAcknowledgementLabel,
  lastMessageLabel,
  communityTypeLabel,
} from './communityCommsInboxVocabulary';

/** Poll cadence — mirrors the other coordination trays (a read on a stable view). */
const POLL_MS = 30_000;

/** Compact, locale-free `MM-DD HH:MM UTC` from an ISO stamp (mirrors CommunityCommsThread). */
function fmtTime(iso: string): string {
  return iso.length >= 16 ? `${iso.slice(5, 10)} ${iso.slice(11, 16)} UTC` : iso;
}

export function CommunityCoordinationInboxPanel({
  onActiveCountChange,
}: {
  /** Report the operator's OWN total not-yet-acknowledged count up for the sub-tab badge. On
   *  error/loading this is 0 (the badge renders nothing at 0 — unknown is NEVER a "0" badge). */
  onActiveCountChange?: (count: number) => void;
}): JSX.Element {
  // rows === null ⇒ not yet loaded (drives the loading state). error is a distinct flag so a
  // failure can NEVER collapse to the empty state.
  const [rows, setRows] = useState<CommunityCoordinationInboxRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback((signal?: AbortSignal) => {
    setLoading(true);
    setError(false);
    fetchCommunityCoordinationInbox(signal)
      .then((r) => {
        setRows(r);
        setLoading(false);
      })
      .catch(() => {
        if (signal?.aborted) return;
        setError(true);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const ctl = new AbortController();
    load(ctl.signal);
    const id = window.setInterval(() => load(), POLL_MS);
    return () => {
      ctl.abort();
      window.clearInterval(id);
    };
  }, [load, refreshKey]);

  // The per-tab badge = the operator's OWN total not-yet-acknowledged messages. On error/loading
  // (rows null) report 0 so the badge stays ABSENT — an unknown is never surfaced as a "0" badge.
  useEffect(() => {
    const total = rows ? rows.reduce((sum, r) => sum + r.unread_count, 0) : 0;
    onActiveCountChange?.(total);
  }, [rows, onActiveCountChange]);

  return (
    <div className="px-3 py-3" data-testid="community-coordination-inbox">
      <p className="mb-2 text-[11px] leading-relaxed text-slate-400">
        {COMMUNITY_COORDINATION_INTRO}
      </p>

      {error ? (
        <p className="text-[12px] text-rose-300" data-testid="community-coordination-error">
          {COMMUNITY_COORDINATION_ERROR}{' '}
          <button
            type="button"
            data-testid="community-coordination-retry"
            onClick={() => setRefreshKey((k) => k + 1)}
            className="underline hover:text-rose-200"
          >
            retry
          </button>
        </p>
      ) : loading && rows === null ? (
        <p className="text-[12px] text-slate-500" data-testid="community-coordination-loading">
          {COMMUNITY_COORDINATION_LOADING}
        </p>
      ) : rows && rows.length === 0 ? (
        <p className="text-[12px] text-slate-500" data-testid="community-coordination-empty">
          {COMMUNITY_COORDINATION_EMPTY}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="community-coordination-list">
          {rows?.map((row) => (
            <li key={row.community_id} data-testid="community-coordination-row">
              {/* Each row OPENS the real #420a thread (selectOnCop → CommunityBody), never a
                  dead-end badge. Same neutral card for every community_type (FN-dignity peer). */}
              <button
                type="button"
                data-testid="community-coordination-row-btn"
                data-community-id={row.community_id}
                data-community-type={row.community_type}
                onClick={() => selectOnCop({ kind: 'community', id: row.community_id })}
                className="flex w-full items-center gap-2 rounded border border-slate-700 bg-slate-800/40 px-3 py-2 text-left hover:border-slate-500 hover:bg-slate-800/70"
              >
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-slate-100">
                      {row.name}
                    </span>
                    {row.unread_count > 0 && (
                      <span
                        data-testid="community-coordination-unread"
                        className="shrink-0 rounded-full bg-cyan-600 px-1.5 py-0.5 text-[10px] font-semibold text-white"
                      >
                        {awaitingAcknowledgementLabel(row.unread_count)}
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 flex items-center gap-2 text-[10px] text-slate-500">
                    <span
                      data-testid="community-coordination-type"
                      className="rounded bg-slate-700/70 px-1.5 py-0.5 text-slate-300"
                    >
                      {communityTypeLabel(row.community_type)}
                    </span>
                    <span className="truncate">{lastMessageLabel(fmtTime(row.last_activity_at))}</span>
                  </span>
                  {/* Migration 0154 — when the thread's latest hazard-linked message names a fire,
                      show WHICH fire is waiting on this seat (so the receiving seat can act without
                      opening the thread). Absent when no message carries a hazard link. */}
                  {row.latest_hazard && (
                    <span
                      data-testid="community-coordination-hazard"
                      data-hazard-source-type={row.latest_hazard.source_type}
                      data-hazard-source-id={row.latest_hazard.source_id}
                      className="mt-0.5 block truncate text-[10px] font-medium text-amber-300/80"
                    >
                      {hazardProvenanceLabel(
                        row.latest_hazard.source_type,
                        row.latest_hazard.source_ref,
                      )}
                    </span>
                  )}
                </span>
                <span aria-hidden className="shrink-0 text-slate-400">
                  →
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Reuse the #420a register — this is INTERNAL operator coordination, not resident-facing,
          and the count is the operator's own queue, never a watch on a community. */}
      <p className="mt-3 text-[9px] text-slate-600" data-testid="community-coordination-register">
        {OPERATOR_COORDINATION_DISCLAIMER}
      </p>
    </div>
  );
}
